import { useState } from 'react';
import { Autocomplete, autocompleteClasses, buttonBaseClasses, Checkbox, CircularProgress, Icon, InputAdornment, TextField } from '@mui/material';

import isEmpty from 'Lib/utilities/isEmpty';

import HelperText from '../form/HelperText';

export default function BaseSelect({ async, filterOptions, TextFieldProps = {}, label, multiple, onTextChange, helperText, placeholder, required, startAdornment, ...rest }) {
	const [open, setOpen] = useState(false);

	const getOptionLabel = option => {
		if (isEmpty(option)) return '';

		if (rest.getOptionLabel) {
			return rest.getOptionLabel(option);
		}

		// Label needs to be returned as a string in instances where numbers are passed through
		return option.label ? `${option.label}` : '';
	};

	return (
		<Autocomplete
			{...rest}
			data-cy={label}
			PaperComponent={rest.PaperComponent}
			autoHighlight
			getOptionLabel={getOptionLabel}
			disableCloseOnSelect={!!multiple}
			disableClearable={isEmpty(rest.value)}
			isOptionEqualToValue={(option, value) => option.value == value.value}
			value={rest.value ?? null}
			multiple={multiple}
			open={open}
			onOpen={() => {
				setOpen(true);
				if (rest.onOpen) rest.onOpen();
			}}
			onClose={() => {
				setOpen(false);
				if (rest.onClose) rest.onClose();
			}}
			limitTags={1}
			// disablePortal={multiple ? true : false}
			getLimitTagsText={amount => `+${amount} more`}
			ChipProps={{ deleteIcon: <Icon>close</Icon> }}
			{...(async && { filterOptions: filterOptions ? filterOptions : x => x })}
			renderOption={
				rest.renderOption
					? (optionProps, option, { selected }) => rest.renderOption(optionProps, option, selected)
					: (optionProps, option, { selected }) =>
							multiple ? (
								<li {...optionProps} data-cy={getOptionLabel(option)} key={optionProps.id} style={{ backgroundColor: 'unset', padding: 0 }}>
									<Checkbox icon={<Icon>check_box_outline_blank</Icon>} checkedIcon={<Icon>check_box</Icon>} style={{ marginRight: 8 }} checked={selected} />
									{getOptionLabel(option)}
								</li>
							) : (
								<li {...optionProps} data-cy={getOptionLabel(option)} key={optionProps.id}>
									{getOptionLabel(option)}
								</li>
							)
			}
			renderInput={params => {
				const showPlaceholder = isEmpty(params.InputProps.startAdornment);

				return (
					<TextField
						{...TextFieldProps}
						{...params}
						autoComplete="off"
						variant="outlined"
						label={label}
						required={required}
						placeholder={showPlaceholder ? placeholder : ''}
						onChange={onTextChange}
						sx={{
							[`& .${autocompleteClasses.endAdornment}`]: {
								[`& .${buttonBaseClasses.root}`]: {
									width: '24px',
									height: '24px'
								}
							}
						}}
						error={!!rest.meta?.error}
						helperText={<HelperText meta={rest.meta} required={required} helperText={helperText} />}
						InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
						InputProps={{
							...params.InputProps,
							startAdornment: (
								<>
									<InputAdornment position="start">
										<Icon sx={{ mr: 1 }}>{startAdornment || 'search'}</Icon>
									</InputAdornment>
									{params.InputProps.startAdornment}
								</>
							),
							endAdornment: rest.loading ? <CircularProgress color="inherit" size={20} /> : params.InputProps.endAdornment
						}}
					/>
				);
			}}
		/>
	);
}
